import { FC, useState } from 'react';
import { IonButton } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { PaperPlaneIcon } from '../../theme/Icons';
import { useBreakpoints } from '../../theme/useBreakpoints';
import { BRAND } from './agencyChartTheme';
import { InsightsBeaOpener } from './InsightsBeaOpener';

const PROMPT = 'Message Bea about your clients';
const AVATAR = '/assets/images/help/helper-bea.png';

const useStyles = createUseStyles({
    box: {
        alignSelf: 'center',
        width: '100%',
        maxWidth: 760,

        '@media print': {
            display: 'none'
        }
    },

    composer: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '6px 6px 6px 12px',
        borderRadius: 16,
        border: `1px solid ${BRAND.tealEdge}`,
        background: 'var(--ion-background-color)',

        '&:focus-within': {
            borderColor: 'var(--ion-color-primary)',
            boxShadow: '0 0 0 3px rgba(var(--ion-color-primary-rgb), 0.12)'
        }
    },

    avatar: {
        width: 34,
        height: 34,
        flexShrink: 0,
        borderRadius: '50%',
        objectFit: 'cover' as const
    },

    input: {
        flex: 1,
        minWidth: 0,
        border: 0,
        outline: 'none',
        background: 'transparent',
        font: 'inherit',
        fontSize: 15,
        color: 'var(--ion-text-color)'
    },

    send: {
        margin: 0,
        '--border-radius': '12px',
        '--padding-start': '13px',
        '--padding-end': '13px',
        height: 44
    }
});

/**
 * The box above the reports for asking agency Bea about the caseload. On a wide window it takes the
 * message: sending opens her with the question already asked, and sending nothing just opens her. On a
 * narrow one it is a single button that opens her full screen.
 */
export const InsightsMessageBea: FC<{ onAsk: (question?: string) => void }> = ({ onAsk }) => {
    const classes = useStyles();
    const breakpoints = useBreakpoints();
    const [draft, setDraft] = useState('');

    if (!breakpoints.isAboveBreakpoint('md')) {
        return <InsightsBeaOpener prompt={PROMPT} avatar={AVATAR} onOpen={() => onAsk()} />;
    }

    const send = () => {
        onAsk(draft.trim() || undefined);
        setDraft('');
    };

    return (
        <div className={classes.box} data-id="insights-ask-bea-box">
            <div className={classes.composer}>
                <img src={AVATAR} alt="" className={classes.avatar} />
                <input
                    className={classes.input}
                    value={draft}
                    aria-label={PROMPT}
                    placeholder={PROMPT}
                    data-id="insights-ask-bea-input"
                    onChange={event => setDraft(event.target.value)}
                    onKeyDown={event => {
                        // Safari commits an IME composition with an Enter that has isComposing false and keyCode 229.
                        if (event.key === 'Enter' && !event.nativeEvent.isComposing && event.nativeEvent.keyCode !== 229) {
                            event.preventDefault();
                            send();
                        }
                    }}
                />
                <IonButton className={classes.send} color="primary" aria-label="Send to Bea" data-id="insights-ask-bea" onClick={send}>
                    <PaperPlaneIcon color="light" />
                </IonButton>
            </div>
        </div>
    );
};
